import {
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import AlertPanel from 'ext-ui/components/AlertPanel/AlertPanel'
import CloseButton from 'ext-ui/components/CloseButton/CloseButton'
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import {
  selectCreditCardIdFromRoute,
  selectFilteredRecentUserEmailOptionsWithoutBudgetUsersOrGuests,
  selectFilteredUserEmailOptionsWithoutBudgetUsersOrGuests,
  selectUpdateBudgetError,
  selectUsersFullPaginatedData,
} from 'src/app/BudgetOverview/redux/budgetOverview.selectors'
import Box from 'src/components/Box/Box'
import BudgetUsersForm from 'src/components/BudgetUsersBox/BudgetUsersForm'
import HoverTooltip from 'src/components/HoverTooltip/HoverTooltip'
import UserAvatar from 'src/components/UserAvatar/UserAvatar'
import { UserDropdownInfiniteScrollProps } from 'src/components/UserDropdown/UserDropdown'
import { AvatarSizes } from 'src/enums'
import { useTranslate } from 'src/localization/Trns'
import { CreditCardListModel } from 'src/models/creditCard.models'
import { CreditCardPermissionsModel } from 'src/models/creditCardPermissions.models'
import { UserAssociation, UserListModel } from 'src/models/user.models'
import { budgetOverviewUrl, peopleMembersDetailUrl } from 'src/navigation/urls'
import { useQueryOrganizationFeatureFlags } from 'src/queries/organizations/featureflags.queries'
import { createSetIsRemoveBudgetUserInvitePopupOpen } from 'src/redux/actions/app.actions'
import {
  deleteBudgetUsers,
  updateBudgetUsers,
} from 'src/redux/actions/data/creditCards.actions'
import { getRecentUsers, getUsers } from 'src/redux/actions/data/users.actions'
import { selectUserIdFromState } from 'src/redux/selectors/auth.selectors'
import { selectUserExplicitOrganizationListModelFromState } from 'src/redux/selectors/data/organizations.selectors'
import { PutCreditCardRequest } from 'src/requests/putCreditCard.request'
import styled, { css } from 'styled-components'

interface BudgetUsersBoxProps {
  creditCard: CreditCardListModel
  budgetPermissions?: CreditCardPermissionsModel
}

const StyledDiv = styled.div`
  align-items: center;
  gap: 8px;
  align-self: stretch;
  height: 40px;
`

const StyledPill = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  ${getFontCss(10, '#856700', styleConstants.fontFamilies.medium)};
  background-color: #fcf1ca;
  border-radius: ${styleConstants.borderRadius.box};
  padding: 0px 4px;
`

const BudgetUsersBox: React.FC<BudgetUsersBoxProps> = (
  props,
): React.ReactElement => {
  const translate = useTranslate()

  const dispatch = useDispatch()
  const [displayAlert, setDisplayAlert] = useState<boolean>(false)
  const userId = useSelector(selectUserIdFromState)
  const usersFullPaginatedData = useSelector(selectUsersFullPaginatedData)
  const filteredRecentUserEmailOptionsWithoutBudgetUsersOrGuests = useSelector(
    selectFilteredRecentUserEmailOptionsWithoutBudgetUsersOrGuests,
  )
  const filteredUserEmailOptionsWithoutBudgetUsersOrGuests = useSelector(
    selectFilteredUserEmailOptionsWithoutBudgetUsersOrGuests,
  )
  const id = useSelector(selectCreditCardIdFromRoute)
  const updateBudgetError = useSelector(selectUpdateBudgetError)
  const orgId = useSelector(
    selectUserExplicitOrganizationListModelFromState,
  )?.id
  const { data: orgFeatureFlags } = useQueryOrganizationFeatureFlags({ orgId })
  const { analytics } = useAnalyticsContext()

  const canUpdateBudgetUsers = props.budgetPermissions?.canUpdateCreditCard
  const ownerOrgId = props.creditCard.organizationId
  const convertedUsers =
    props.creditCard.budgetUsers?.map((user) => {
      return new UserListModel(user)
    }) ?? []

  useEffect(() => {
    dispatch(getRecentUsers(UserAssociation.virtualCardTo))
    dispatch(getRecentUsers(UserAssociation.invitationTo))
    dispatch(getRecentUsers(UserAssociation.delegateCreditCardTo))
  }, [])

  const fetchUsers = (filterInput?: string, page?: number): void => {
    dispatch(
      getUsers(
        {
          q: filterInput,
          count: 10,
          page: page,
        },
        true,
      ),
    )
  }

  const userInfiniteScrollProps: UserDropdownInfiniteScrollProps = {
    fetch: fetchUsers,
    ...usersFullPaginatedData,
  }

  const sortBudgetUsers = (
    unsortedUsers: Array<UserListModel | undefined>,
  ): Array<UserListModel | undefined> => {
    const sortedUsersNotInOrg = unsortedUsers
      .map((user) => {
        if (user?.organizationId !== ownerOrgId) return user
      })
      .sort((a, b) => {
        if (a === undefined) return 1
        if (b === undefined) return -1
        return a.firstNameOrEmail.localeCompare(b.firstNameOrEmail)
      })
    const sortedUsersInOrg = unsortedUsers
      .map((user) => {
        if (user?.organizationId === ownerOrgId) return user
      })
      .sort((a, b) => {
        if (a === undefined) return 1
        if (b === undefined) return -1
        return a.firstNameOrEmail.localeCompare(b.firstNameOrEmail)
      })
    return [...sortedUsersNotInOrg, ...sortedUsersInOrg]
  }
  const sortedUsers = sortBudgetUsers(convertedUsers)

  const toggleAlert = (hasUsers: boolean): void => {
    setDisplayAlert(hasUsers)
  }

  const dispatchPutCreditCard = (body: Partial<PutCreditCardRequest>): void => {
    const emailBody = body.emails ? [...body.emails] : []
    const existingUsers =
      props.creditCard.budgetUsers?.map((user) => user.email) ?? []
    const emailsList = [...existingUsers, ...emailBody]
    dispatch(updateBudgetUsers(id, emailsList, false))
  }

  const [hover, setHover] = useState<boolean>(false)

  return (
    <Box
      noPadding
      css={css`
        display: flex;
        width: 620px;
        padding: 40px;
        flex-direction: column;
        align-items: flex-start;
        gap: 40px;
      `}
    >
      <div
        css={css`
          display: flex;
          gap: 20px;
          flex-direction: column;
        `}
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            align-self: stretch;
            gap: 12px;
          `}
        >
          <span
            css={css`
              ${getFontCss(
                20,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.medium,
              )};
            `}
          >
            {translate('Budget Users')}
          </span>
          <span
            css={css`
              ${getFontCss(
                12,
                styleConstants.colors.gray_darkest,
                styleConstants.fontFamilies.regular,
              )};
              display: flex;
              flex-direction: column;
            `}
          >
            {canUpdateBudgetUsers ? (
              <>
                <span>
                  {translate(
                    'Adding someone will make them a budget user and allow them to create and update virtual cards from this budget. Only people within your org (excluding Guests) can be a budget user, and budgets must always have at least one user. Adding someone outside of your org will invite them as a Member to join your org.',
                  )}
                </span>
                {orgFeatureFlags?.cardManagerRoleEnabled && (
                  <span
                    css={css`
                      margin-top: 8px;
                    `}
                  >
                    {translate(
                      'Note: the Owner, Admins and select Card Managers will have access to edit budget details and issue virtual cards',
                    )}
                  </span>
                )}
              </>
            ) : (
              translate(
                'As a budget user, you have access to create and update virtual cards on this budget',
              )
            )}
          </span>
        </div>
        {canUpdateBudgetUsers && (
          <div
            css={css`
              display: flex;
              align-items: start;
              gap: 20px;
              align-self: stretch;
              flex-direction: column;
              flex-shrink: 1;
            `}
          >
            <div
              css={css`
                width: 100%;
              `}
            >
              <BudgetUsersForm
                userOptions={filteredUserEmailOptionsWithoutBudgetUsersOrGuests}
                recentUserOptions={
                  filteredRecentUserEmailOptionsWithoutBudgetUsersOrGuests
                }
                userInfiniteScrollProps={userInfiniteScrollProps}
                displayAlert={displayAlert}
                toggleAlert={toggleAlert}
                putCreditCard={dispatchPutCreditCard}
                creditCard={props.creditCard}
              />
              {updateBudgetError && (
                <span
                  css={css`
                    ${getFontCss(
                      12,
                      styleConstants.colors.red_dark,
                      styleConstants.fontFamilies.regular,
                    )}
                  `}
                >
                  {updateBudgetError}
                </span>
              )}
            </div>
          </div>
        )}
        {displayAlert && (
          <AlertPanel
            variant="warning"
            css={css`
              padding: 12px 20px;
            `}
          >
            <span
              css={css`
                ${getFontCss(
                  12,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.regular,
                )};
              `}
            >
              {translate(
                "You've added people outside of your org as budget users. They must accept their org invite as a Member in order to access this budget.",
              )}
            </span>
          </AlertPanel>
        )}
      </div>
      <div
        css={css`
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 20px;
          align-self: stretch;
        `}
      >
        {sortedUsers &&
          sortedUsers.map((user) => {
            const allowDelete =
              (props.creditCard.budgetUsers?.length ?? 0) > 1 ||
              user?.id !== props.creditCard?.ownerId
            return (
              user !== undefined && (
                <StyledDiv key={user.id}>
                  <div
                    css={css`
                      display: flex;
                      align-items: center;
                      gap: 8px;
                      align-self: stretch;
                    `}
                  >
                    <UserAvatar user={user} size={AvatarSizes.Medium} />
                    <div
                      css={css`
                        display: flex;
                        flex-direction: column;
                        align-items: flex-start;
                        gap: 4px;
                        flex: 1 0 0;
                      `}
                    >
                      <div
                        css={css`
                          display: flex;
                          align-items: center;
                          gap: 8px;
                        `}
                      >
                        <Link
                          css={css`
                            overflow: hidden;
                            text-overflow: ellipsis;
                            white-space: nowrap;
                            ${getFontCss(
                              16,
                              themeConstants.colors.cta,
                              styleConstants.fontFamilies.medium,
                            )}
                            text-decoration: underline;
                          `}
                          to={peopleMembersDetailUrl(user.id)}
                          onClick={(): void => {
                            analytics?.track(
                              "budget details page - select a budget user's name - text link press",
                              {
                                interaction: 'text link press',
                                intent: "select a budget user's name",
                                screen: budgetOverviewUrl(props.creditCard.id),
                                typeOfBudget: props.creditCard.recurrence
                                  ? 'Recurring'
                                  : props.creditCard.isLimitedBudget
                                    ? 'Standard'
                                    : 'Unlimited',
                                budgetName: props.creditCard.displayName,
                                userEmail: user.email,
                                userName: user.fullNameOrEmail,
                                userRole: user.orgRoleDisplayTitle,
                                isUserBudgetCreator:
                                  user.id === props.creditCard.ownerId,
                              },
                            )
                          }}
                        >
                          {user.firstName && user.lastName
                            ? user.firstName + ' ' + user.lastName + ' '
                            : null}
                          {user.id === userId && (
                            <span>{translate('(You)')}</span>
                          )}
                        </Link>
                        {user?.organizationId !== ownerOrgId && (
                          <div
                            css={css`
                              display: flex;
                              align-items: center;
                            `}
                          >
                            <StyledPill>
                              {translate('Pending Org Invite')}
                            </StyledPill>
                          </div>
                        )}
                      </div>
                      <span
                        id={user.email}
                        css={css`
                          overflow: hidden;
                          text-overflow: ellipsis;
                          ${getFontCss(
                            12,
                            styleConstants.colors.gray_darkest,
                            styleConstants.fontFamilies.regular,
                          )}
                        `}
                      >
                        {user.email}
                      </span>
                    </div>
                    {canUpdateBudgetUsers ? (
                      allowDelete ? (
                        <CloseButton
                          variant="dark-on-light"
                          onClick={(): void => {
                            if (
                              props.creditCard.budgetUsers?.length === 1 &&
                              user?.id !== props.creditCard?.ownerId
                            ) {
                              dispatch(
                                createSetIsRemoveBudgetUserInvitePopupOpen(
                                  true,
                                  props.creditCard,
                                  user,
                                ),
                              )
                            } else {
                              dispatch(
                                deleteBudgetUsers(
                                  props.creditCard.id,
                                  [user.email],
                                  user.firstName,
                                  user.lastName,
                                ),
                              )
                              analytics?.track('remove budget user', {
                                budget_id: props.creditCard?.id,
                                user_email:
                                  user.email !== undefined
                                    ? user.email.split('@')[1]
                                    : '',
                                issuer: props.creditCard?.issuer,
                              })
                            }
                          }}
                        />
                      ) : (
                        <div
                          onMouseEnter={(): void => setHover(true)}
                          onMouseLeave={(): void => setHover(false)}
                          css={css`
                            position: relative;
                          `}
                        >
                          <CloseButton variant="dark-on-light" disabled />
                          <HoverTooltip
                            css={css`
                              left: 25px;
                            `}
                            show={hover}
                            tooltipText={translate(
                              'Budgets must have at least one user',
                            )}
                          />
                        </div>
                      )
                    ) : null}
                  </div>
                </StyledDiv>
              )
            )
          })}
      </div>
    </Box>
  )
}

export default BudgetUsersBox
